/**
 * Loading skeletons — calm, content-shaped placeholders that replace bare
 * spinners on the async Dashboard / Vehicles / Vehicle Detail areas.
 *
 * Thin presentational primitives (no branching logic), so per the build
 * directive they carry no dedicated Vitest test. Every block bottoms out in
 * `Skeleton`, which exposes `data-testid="skeleton"` (the BDD test hook) and a
 * `skeleton-pulse` class whose CSS pulse is disabled under
 * `prefers-reduced-motion` (see styles.css).
 */
import { Card, YStack, type YStackProps } from 'tamagui';

const SKELETON_CLASS = 'skeleton-pulse';
const LINE_HEIGHT_PX = 16;
const TITLE_HEIGHT_PX = 24;
const HEADING_HEIGHT_PX = 32;
const DEFAULT_HEADING_WIDTH_PX = 180;
// Tapering line widths so stacked lines read as text, not a solid block.
const LINE_WIDTHS = ['90%', '65%'] as const;

/** A single pulsing placeholder bar. Sizing/shape come from props. */
export function Skeleton(props: YStackProps) {
  return (
    <YStack
      className={SKELETON_CLASS}
      // data-testid is the BDD hook the skeleton scenarios assert on.
      data-testid="skeleton"
      backgroundColor="$color5"
      height={LINE_HEIGHT_PX}
      borderRadius="$3"
      {...props}
    />
  );
}

/** Stacked, tapering placeholder lines — the shape of a short text block. */
export function SkeletonText({ lines = LINE_WIDTHS.length, ...props }: YStackProps & { lines?: number }) {
  return (
    <YStack gap="$2" {...props}>
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton key={`line-${i}`} width={LINE_WIDTHS[i % LINE_WIDTHS.length]} />
      ))}
    </YStack>
  );
}

/** Card-shaped placeholder: a title bar over a short text block. */
export function SkeletonCard(props: YStackProps) {
  return (
    <Card padding="$4" elevate width="100%" {...props}>
      <YStack gap="$3">
        <Skeleton height={TITLE_HEIGHT_PX} width="70%" />
        <SkeletonText />
      </YStack>
    </Card>
  );
}

/**
 * Page-level loading shell: a heading bar over card placeholders. Pass
 * `children` to supply a custom card area (e.g. a wrapped grid); otherwise two
 * stacked `SkeletonCard`s are rendered.
 */
export function SkeletonPage({
  headingWidth = DEFAULT_HEADING_WIDTH_PX,
  children,
  ...props
}: YStackProps & { headingWidth?: number }) {
  return (
    <YStack gap="$4" {...props}>
      <Skeleton height={HEADING_HEIGHT_PX} width={headingWidth} />
      {children ?? (
        <>
          <SkeletonCard />
          <SkeletonCard />
        </>
      )}
    </YStack>
  );
}
